import { ref } from 'vue';
import { aiErrorMessage } from '@/composables/aiErrorMessage';

/**
 * Standard wrapper for a synchronous AI request (Lead Summary, Draft Reply, …).
 * Centralises loading + error handling so every AI surface fails the same elegant
 * way: a rejected request never breaks the feature, the agent sees what happened,
 * and `retryable` drives whether a Retry affordance is offered.
 *
 * The message + retryable flag come from the backend AiFailure envelope
 * ({ message, reason, retryable }). Network / unknown errors (no envelope) default
 * to retryable, since trying again is reasonable. Blocked / DNC results are NOT
 * errors — they come back 200 with contactable=false and stay the caller's concern.
 *
 * Usage:
 *   const { loading, error, retryable, run } = useAiRequest();
 *   const res = await run(() => window.axios.post(route('leads.ai.summary', id)));
 *   if (!res) return; // failed — `error` is set, feature stays usable
 */
export function useAiRequest() {
    const loading = ref(false);
    const error = ref(null);
    const retryable = ref(true);

    function reset() {
        error.value = null;
        retryable.value = true;
    }

    async function run(fn) {
        // Re-entrancy guard: ignore a second call while one is in flight, so a
        // double-click (or an auto-load racing a Retry) can't fire two requests.
        if (loading.value) return null;

        loading.value = true;
        reset();

        try {
            return await fn();
        } catch (e) {
            const data = e?.response?.data ?? {};
            // Only ever show a message we know is safe (our envelope or a 422); a raw
            // 500 / debug payload is hidden behind the generic fallback.
            error.value = aiErrorMessage(e);
            // Default to retryable for network / unknown errors that carry no envelope.
            retryable.value = data.retryable ?? true;

            return null;
        } finally {
            loading.value = false;
        }
    }

    return { loading, error, retryable, reset, run };
}
